import { z } from 'zod';
import { PUSH_MAX_TTL_SECONDS } from '../push/delivery-options.ts';
import { PushButtonId } from '../push/sdk-payload.ts';

/**
 * Push settings of an environment (BB-77): defaults a message falls back to (`environment_default` in its send
 * settings, BB-73/BB-76), the registry of Android notification channels, iOS notification categories and threads the
 * apps declare, and per-app capabilities. The apps register the same channels and categories at startup — the
 * service generates the snippet.
 */
export const PushEnvironmentSettings = z.strictObject({
  defaultTtlSeconds: z.int().min(0).max(PUSH_MAX_TTL_SECONDS).default(PUSH_MAX_TTL_SECONDS),
  defaultAndroidPriority: z.enum(['normal', 'high']).default('normal'),
  defaultIosInterruptionLevel: z.enum(['passive', 'active', 'time-sensitive']).default('active'),
  defaultSound: z.enum(['default', 'none']).default('default'),
  /** Influenced opens (BB-79): an app open this many minutes after an accepted push, without a tap. Kept when left out. */
  influencedOpenWindowMinutes: z.int().min(1).max(1440).optional(),
});
export type PushEnvironmentSettings = z.infer<typeof PushEnvironmentSettings>;

export const AndroidChannelId = z.string().regex(/^[a-z0-9_]{1,64}$/, 'lowercase letters, digits and _ (up to 64)');

export const AndroidChannelBody = z.strictObject({
  /** Null — every Android app of the environment. */
  appInstanceId: z.uuid().nullable().default(null),
  channelId: AndroidChannelId,
  name: z.string().trim().min(1).max(80),
  description: z.string().max(300).optional(),
  importance: z.enum(['min', 'low', 'default', 'high']).default('default'),
  isDefault: z.boolean().default(false),
  /** Used when a message names no channel or one the device does not have; one per environment. */
  isFallback: z.boolean().default(false),
});
export type AndroidChannelBody = z.infer<typeof AndroidChannelBody>;
export const UpdateAndroidChannelBody = AndroidChannelBody.omit({ appInstanceId: true, channelId: true }).partial();
export type UpdateAndroidChannelBody = z.infer<typeof UpdateAndroidChannelBody>;

export const IOS_CATEGORY_PRESETS = {
  accept_decline: [{ id: 'accept', title: 'Accept', opensApp: true }, { id: 'decline', title: 'Decline', opensApp: false, destructive: true }],
  yes_no: [{ id: 'yes', title: 'Yes', opensApp: true }, { id: 'no', title: 'No', opensApp: false }],
  confirm_cancel: [{ id: 'confirm', title: 'Confirm', opensApp: true }, { id: 'cancel', title: 'Cancel', opensApp: false, destructive: true }],
  more: [{ id: 'more', title: 'More', opensApp: true }],
} as const;
export const IOS_CATEGORY_PRESET_IDS = { accept_decline: 'bb_accept_decline', yes_no: 'bb_yes_no', confirm_cancel: 'bb_confirm_cancel', more: 'bb_more' } as const;

export const IosCategoryButton = z.strictObject({
  id: PushButtonId,
  title: z.string().trim().min(1).max(40),
  opensApp: z.boolean().default(true),
  destructive: z.boolean().default(false),
  authRequired: z.boolean().default(false),
});
export type IosCategoryButton = z.infer<typeof IosCategoryButton>;

export const IosCategoryBody = z
  .strictObject({
    appInstanceId: z.uuid().nullable().default(null),
    categoryId: z.string().regex(/^[A-Za-z0-9_.-]{1,64}$/, 'letters, digits, _ . - (up to 64)'),
    preset: z.enum(Object.keys(IOS_CATEGORY_PRESETS) as [keyof typeof IOS_CATEGORY_PRESETS, ...Array<keyof typeof IOS_CATEGORY_PRESETS>]).nullable().default(null),
    buttons: z.array(IosCategoryButton).max(4).default([]),
  })
  .refine((c) => c.preset !== null || c.buttons.length > 0, { message: 'a category needs a preset or buttons', path: ['buttons'] })
  .refine((c) => new Set(c.buttons.map((b) => b.id)).size === c.buttons.length, { message: 'button ids must be unique', path: ['buttons'] });
export type IosCategoryBody = z.infer<typeof IosCategoryBody>;

export const IosThreadBody = z.strictObject({ threadId: z.string().regex(/^[A-Za-z0-9_.-]{1,64}$/), label: z.string().trim().min(1).max(80) });
export type IosThreadBody = z.infer<typeof IosThreadBody>;

export const AppPushCapabilities = z.strictObject({
  iosTimeSensitiveEnabled: z.boolean().default(false),
  iosNotificationServiceExtension: z.boolean().default(false),
  androidSmallIconRes: z.string().max(100).nullable().default(null),
});
export type AppPushCapabilities = z.infer<typeof AppPushCapabilities>;

export interface AndroidChannelView extends AndroidChannelBody {
  id: string;
  archivedAt: string | null;
  /** Percent (0–100) of active Android installations that reported the channel; null until the SDK reports channels (BB-80). */
  seenOnInstallationsPct: number | null;
}
export interface IosCategoryView extends IosCategoryBody {
  id: string;
  archivedAt: string | null;
}
export interface IosThreadView extends IosThreadBody {
  archivedAt: string | null;
}
export interface SdkConfigSnippet {
  platform: 'android' | 'ios';
  language: 'kotlin' | 'swift';
  code: string;
}
